<script setup>
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { $api } from '@/services';

const props = defineProps({
	studentId: {
		type: [Number, String],
		default: null,
	},
	state: {
		type: String,
		default: '',
	},
});

const emit = defineEmits(['close', 'apply']);

const students = ref([]);
const draftStudentId = ref(props.studentId || '');
const draftState = ref(props.state || '');
const studentOpen = ref(false);
const stateOpen = ref(false);
const studentQuery = ref('');

const stateOptions = [
	{ value: '', label: 'Tất cả trạng thái' },
	{ value: 'draft', label: 'Bản nháp' },
	{ value: 'unsend', label: 'Chưa gửi PH' },
	{ value: 'sent', label: 'Đã gửi PH' },
];

const fold = (value) =>
	(value || '')
		.toString()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/đ/g, 'd');

const selectedStudent = computed(() =>
	students.value.find((item) => String(item.id) === String(draftStudentId.value))
);

const filteredStudents = computed(() => {
	const query = fold(studentQuery.value);
	if (!query) return students.value;
	return students.value.filter((item) => fold(item.name).includes(query));
});

const stateLabel = computed(() => {
	const found = stateOptions.find((item) => item.value === draftState.value);
	return found ? found.label : 'Tất cả trạng thái';
});

onMounted(async () => {
	try {
		const res = await $api.student.getSimpleStudents();
		const body = res?.data;
		students.value = Array.isArray(body) ? body : body?.data || [];
	} catch (err) {
		students.value = [];
	}
});

const chooseStudent = (student) => {
	draftStudentId.value = student ? student.id : '';
	studentOpen.value = false;
	studentQuery.value = '';
};

const apply = () => {
	emit('apply', {
		student_id: draftStudentId.value || '',
		state: draftState.value || '',
	});
};
</script>

<template>
	<div class="fixed inset-0 z-50 flex justify-end" @click.self="emit('close')">
		<div class="absolute inset-0 bg-black bg-opacity-20" @click="emit('close')"></div>
		<aside
			class="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl"
		>
			<div class="flex items-center justify-between px-6 pt-12 pb-6 screening-filter__header">
				<div class="flex items-center gap-2">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M3 6.5C3 5.67 3.67 5 4.5 5h15c.83 0 1.5.67 1.5 1.5S20.33 8 19.5 8h-15C3.67 8 3 7.33 3 6.5Zm2 6c0-.83.67-1.5 1.5-1.5h11c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-11c-.83 0-1.5-.67-1.5-1.5Zm3 4.5c-.83 0-1.5.67-1.5 1.5S7.17 20 8 20h8c.83 0 1.5-.67 1.5-1.5S16.83 17 16 17H8Z"
							fill="#65758B"
						/>
					</svg>
					<div class="text-2xl font-bold text-neutral-700">Bộ lọc</div>
				</div>
				<button
					type="button"
					class="text-neutral-500"
					@click="emit('close')"
				>
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							d="M6 6l12 12M18 6L6 18"
							stroke="currentColor"
							stroke-width="1.8"
							stroke-linecap="round"
						/>
					</svg>
				</button>
			</div>
			<div class="flex-1 overflow-y-auto px-6 py-6 space-y-10">
				<div>
					<div class="text-sm font-semibold text-neutral-600 mb-1">
						Lọc theo học sinh
					</div>
					<button
						type="button"
						class="screening-select"
						@click="
							studentOpen = !studentOpen;
							stateOpen = false;
						"
					>
						<span :class="selectedStudent ? 'text-neutral-700' : 'text-neutral-400'">
							{{ selectedStudent ? selectedStudent.name : 'Tên học sinh' }}
						</span>
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
							<path d="M5 8l5 5 5-5" stroke="#94A3B8" stroke-width="1.6" stroke-linecap="round" />
						</svg>
					</button>
					<div v-if="studentOpen" class="screening-menu">
						<input
							v-model="studentQuery"
							type="text"
							class="screening-menu__search"
							placeholder="Tìm theo tên học sinh"
						/>
						<button
							type="button"
							class="screening-menu__item text-neutral-400"
							@click="chooseStudent(null)"
						>
							Tất cả học sinh
						</button>
						<button
							v-for="student in filteredStudents"
							:key="student.id"
							type="button"
							class="screening-menu__item"
							@click="chooseStudent(student)"
						>
							{{ student.name }}
						</button>
						<div
							v-if="filteredStudents.length === 0"
							class="px-4 py-3 text-sm text-neutral-400"
						>
							Không tìm thấy học sinh
						</div>
					</div>
				</div>
				<div>
					<div class="text-sm font-semibold text-neutral-600 mb-1">
						Lọc theo trạng thái
					</div>
					<button
						type="button"
						class="screening-select"
						@click="
							stateOpen = !stateOpen;
							studentOpen = false;
						"
					>
						<span :class="draftState ? 'text-neutral-700' : 'text-neutral-400'">{{ stateLabel }}</span>
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
							<path d="M5 8l5 5 5-5" stroke="#94A3B8" stroke-width="1.6" stroke-linecap="round" />
						</svg>
					</button>
					<div v-if="stateOpen" class="screening-menu">
						<button
							v-for="option in stateOptions"
							:key="option.value || 'all'"
							type="button"
							class="screening-menu__item"
							@click="
								draftState = option.value;
								stateOpen = false;
							"
						>
							{{ option.label }}
						</button>
					</div>
				</div>
			</div>
			<div class="border-t screening-filter__footer">
				<div class="flex justify-end p-4">
					<button type="button" class="button-p w-fit px-5" @click="apply">
						Áp dụng bộ lọc
					</button>
				</div>
			</div>
		</aside>
	</div>
</template>

<style lang="scss" scoped>
.screening-filter__header {
	background: #eef3f8;
}

.screening-filter__footer {
	border-color: #faf5f1;
}

.screening-select {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 52px;
	padding: 0 16px;
	border-radius: 4px;
	background: #fff;
	border: 0.5px solid #f8fafc;
	box-shadow: 0 0 1px rgba(50, 50, 71, 0.2), 0 1px 2px rgba(50, 50, 71, 0.08);
	font-size: 16px;
	text-align: left;
}

.screening-menu {
	margin-top: 8px;
	max-height: 240px;
	overflow-y: auto;
	background: #fff;
	border-radius: 4px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.screening-menu__search {
	width: 100%;
	padding: 12px 16px;
	border: 0;
	border-bottom: 1px solid #e2e8f0;
	outline: none;
	font-size: 14px;
}

.screening-menu__item {
	display: block;
	width: 100%;
	padding: 10px 16px;
	text-align: left;
	font-size: 14px;
	color: #344256;

	&:hover {
		background: #f8fafc;
	}
}
</style>
